import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import Input from "../../components/common/Input";
import Button from "../../components/common/Button";
import { LoginFormData } from "../../schemas/auth/formData";
import { loginSchema } from "../../schemas/auth/schemas";
import Loader from "../../components/common/Loader";
import ContactFodxpert from "../../components/common/ContactFodxpert";

const Login: React.FC = () => {
  const { login } = useAuth();

  const {
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      username: "",
      password: "",
    },
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await login.mutate(data);
    } catch (error) {
      // Error is handled by the mutation's onError callback
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(onSubmit)();
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      {login.loading ? (
        <Loader fullScreen />
      ) : (
        <div className="max-w-md w-full space-y-8 shadow-lg rounded bg-white p-10">
          <div className="text-center">
            <h2 className="mt-6 text-3xl font-extrabold text-primary-default">
              Sign in to your account
            </h2>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="rounded-md shadow-sm -space-y-px">
              <Input
                label="Username"
                type="text"
                name="username"
                value={watch("username") || ""}
                onChange={(e) => setValue("username", e.target.value, { shouldValidate: false })}
                onBlur={() => trigger("username")}
                error={errors.username}
                autoComplete="username"
                required
                className="rounded-t-md"
                placeholder="Username"
              />
              <Input
                label="Password"
                type="password"
                name="password"
                value={watch("password") || ""}
                onChange={(e) => setValue("password", e.target.value, { shouldValidate: false })}
                onBlur={() => trigger("password")}
                error={errors.password}
                autoComplete="current-password"
                required
                className="rounded-b-md"
                placeholder="Password"
                onKeyDown={handleKeyDown}
              />
            </div>

            <div className="flex items-center justify-end">
              <div className="text-sm">
                <Link
                  to="/forgot-password"
                  className="font-medium text-primary-default hover:text-primary-light"
                >
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                className="w-full"
                variant="primary"
                isLoading={isSubmitting}
              >
                Sign in
              </Button>
            </div>
          </form>
        </div>
      )}
      <ContactFodxpert />
    </div>
  );
};

export default Login;
